import UiConfirmDiscardDialog from '@/shared/ui/ui-modal/UiConfirmDiscardDialog'
import UiCustomModal from '@/shared/ui/ui-modal/UiCustomModal'
import { useFormDirty } from '@/shared/ui/ui-modal/hooks/useFormDirty'
import { useModalFormClose } from '@/shared/ui/ui-modal/hooks/useModalFormClose'
import type { FormInstance } from 'antd'
import type { ComponentProps, ReactNode } from 'react'

interface FormModalProps extends Omit<ComponentProps<typeof UiCustomModal>, 'onCancel'> {
  /** antd Form instance — used to detect dirty state. */
  form: FormInstance
  /** Whether a save/submit operation is in progress. Blocks all close attempts. */
  isSubmitting?: boolean
  /**
   * Set to `false` for read-only / view modals that contain no editable form.
   * The modal will close immediately on any close action without a guard.
   */
  hasForm?: boolean
  /**
   * Whether to prompt confirmation dialog when closing with unsaved changes.
   * Defaults to `false` (dialog is OFF by default; enable only for high-risk / hazardous forms).
   */
  confirmOnClose?: boolean
  /** Called when the modal should actually close (after confirmation if needed). */
  onCancel: () => void
  children: ReactNode
}

/**
 * Modal wrapper for forms with unsaved-changes protection.
 *
 * All closing actions (Cancel button, X, ESC, mask click) are routed
 * through a single `handleClose()`:
 *  1. Submitting → block all close attempts.
 *  2. confirmOnClose === false (default) → close immediately.
 *  3. confirmOnClose === true:
 *      - Form dirty → show "discard changes?" confirmation dialog.
 *      - Form not dirty / no form → close immediately.
 *
 * Usage:
 * ```tsx
 * <UiFormModal
 *   open={open}
 *   form={form}
 *   isSubmitting={isSaving}
 *   onCancel={handleClose}
 *   onOk={() => form.submit()}
 *   title="Thêm mới"
 * >
 *   <UiForm form={form}>...</UiForm>
 * </UiFormModal>
 * ```
 */
export default function UiFormModal({
  form,
  isSubmitting = false,
  hasForm = true,
  confirmOnClose = false,
  onCancel,
  children,
  ...rest
}: FormModalProps) {
  const isDirty = useFormDirty(form)

  const {
    handleClose,
    isConfirmOpen,
    handleConfirmOk,
    handleConfirmCancel,
    maskClosable,
    keyboard,
    closable,
  } = useModalFormClose({
    isDirty,
    isSubmitting,
    hasForm,
    confirmOnClose,
    onConfirmClose: onCancel,
  })

  return (
    <>
      <UiCustomModal
        {...rest}
        onCancel={handleClose}
        mask={{ closable: maskClosable }}
        keyboard={keyboard}
        closable={closable}
      >
        {children}
      </UiCustomModal>

      {confirmOnClose && (
        <UiConfirmDiscardDialog
          open={isConfirmOpen}
          onOk={handleConfirmOk}
          onCancel={handleConfirmCancel}
        />
      )}
    </>
  )
}
